import React, { useEffect, useState, useRef } from "react";
import { useSearchParams, Link } from "react-router-dom";
import { api } from "../lib/api";
import { useApp } from "../context/AppContext";
import { CheckCircle2, XCircle, Clock } from "lucide-react";

export default function OrderSuccess() {
    const [params] = useSearchParams();
    const sessionId = params.get("session_id");
    const { clearCart } = useApp();
    const [state, setState] = useState({ status: "loading", payment_status: null, order: null });
    const attempts = useRef(0);

    useEffect(() => {
        if (!sessionId) {
            setState({ status: "error", payment_status: null, order: null });
            return;
        }
        let active = true;
        const poll = async () => {
            try {
                const r = await api.get(`/checkout/status/${sessionId}`);
                if (!active) return;
                setState(r.data);
                if (r.data.payment_status === "paid") {
                    clearCart();
                    return;
                }
                if (r.data.status === "expired") return;
                if (attempts.current < 6) {
                    attempts.current += 1;
                    setTimeout(poll, 2000);
                }
            } catch (e) {
                if (!active) return;
                setState({ status: "error", payment_status: null, order: null });
            }
        };
        poll();
        return () => { active = false; };
    }, [sessionId, clearCart]);

    const paid = state.payment_status === "paid";
    const expired = state.status === "expired";
    const errored = state.status === "error";

    return (
        <div className="px-6 md:px-12 lg:px-24 py-24 max-w-3xl mx-auto" data-testid="order-success-page">
            <div className="panel p-10 text-center">
                {paid ? (
                    <>
                        <CheckCircle2 size={48} className="mx-auto text-[color:var(--ms-sage-dark)]" />
                        <div className="overline mt-6">Ordine Confermato</div>
                        <h1 className="font-display text-4xl text-[color:var(--ms-charcoal)] mt-3">Grazie di cuore.</h1>
                        <p className="text-[color:var(--ms-muted)] mt-3">Il tuo ordine è stato ricevuto. Spediamo entro 24h in tutta Italia.</p>
                    </>
                ) : expired || errored ? (
                    <>
                        <XCircle size={48} className="mx-auto text-[color:var(--ms-terracotta)]" />
                        <div className="overline mt-6">Problema con il pagamento</div>
                        <h1 className="font-display text-4xl text-[color:var(--ms-charcoal)] mt-3">{expired ? "Sessione scaduta" : "Impossibile verificare"}</h1>
                        <p className="text-[color:var(--ms-muted)] mt-3">Ti invitiamo a ripetere il checkout.</p>
                    </>
                ) : (
                    <>
                        <Clock size={48} className="mx-auto text-[color:var(--ms-sage-dark)] animate-pulse" />
                        <div className="overline mt-6">Elaborazione</div>
                        <h1 className="font-display text-4xl text-[color:var(--ms-charcoal)] mt-3">Verifica pagamento…</h1>
                        <p className="text-[color:var(--ms-muted)] mt-3">Ci vorranno pochi secondi.</p>
                    </>
                )}

                {state.order && (
                    <div className="text-left mt-10 pt-8 border-t border-[color:var(--ms-border)]" data-testid="order-summary">
                        <div className="overline mb-4">Dettagli Ordine</div>
                        <div className="text-xs text-[color:var(--ms-muted)]">N° Ordine: <span className="text-[color:var(--ms-charcoal)] font-mono">{state.order.id.slice(0, 8)}</span></div>
                        <ul className="mt-5 divide-y divide-[color:var(--ms-border)]">
                            {state.order.items.map((it, i) => (
                                <li key={i} className="py-3 flex justify-between text-sm">
                                    <span className="text-[color:var(--ms-charcoal)]">{it.title} <span className="text-[color:var(--ms-muted)]">· Taglia {it.size} · x{it.quantity}</span></span>
                                    <span className="text-[color:var(--ms-charcoal)]">€{(it.price * it.quantity).toFixed(2)}</span>
                                </li>
                            ))}
                        </ul>
                        {state.order.discount > 0 && (
                            <div className="flex justify-between text-sm py-2 text-[color:var(--ms-terracotta)]">
                                <span>Sconto</span><span>-€{state.order.discount.toFixed(2)}</span>
                            </div>
                        )}
                        <div className="flex justify-between text-base text-[color:var(--ms-charcoal)] font-medium pt-3 border-t border-[color:var(--ms-border)] mt-3">
                            <span>Totale</span><span>€{state.order.total.toFixed(2)}</span>
                        </div>
                    </div>
                )}

                <div className="mt-10 flex gap-3 justify-center flex-wrap">
                    <Link to="/shop" className="border border-[color:var(--ms-charcoal)] text-[color:var(--ms-charcoal)] px-6 py-3 text-xs tracking-[0.22em] uppercase hover:bg-[color:var(--ms-charcoal)] hover:text-[color:var(--ms-warm-white)] transition-colors" data-testid="order-continue-shop">Continua a shoppare</Link>
                    <Link to="/account" className="bg-[color:var(--ms-charcoal)] text-[color:var(--ms-warm-white)] px-6 py-3 text-xs tracking-[0.22em] uppercase hover:bg-[color:var(--ms-sage-dark)] transition-colors" data-testid="order-view-orders">I miei ordini</Link>
                </div>
            </div>
        </div>
    );
}
